"use client";

import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { usePostHog } from "@posthog/next";
import { api } from "@repo/backend/api";
import { webAnalyticsSession } from "@/lib/briefpanel-analytics-storage";

export function BriefpanelAnalyticsTracker() {
  const posthog = usePostHog();
  const touchVisitor = useMutation(api.analytics.identity.touchVisitor);
  const [visitorId, setVisitorId] = useState<string | null>(() =>
    webAnalyticsSession.peekVisitorId(),
  );

  useEffect(() => {
    let cancelled = false;
    const search = currentSearch();

    void (async () => {
      await webAnalyticsSession.captureClickIds(search);
      const id = await webAnalyticsSession.resolveVisitorId(search);
      if (!cancelled) {
        setVisitorId(id);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);


  useEffect(() => {
    if (!visitorId) {
      return;
    }

    let cancelled = false;
    // Snapshot the search synchronously: a navigation could change the URL while
    // the await below is in flight, which would otherwise read the wrong utm.
    const search = currentSearch();
    void (async () => {
      const clickIds = await webAnalyticsSession.getFreshClickIds();
      if (cancelled) {
        return;
      }

      void touchVisitor({
        visitorId,
        platform: "web",
        ...(clickIds?.gclid ? { gclid: clickIds.gclid } : {}),
        ...(clickIds?.gbraid ? { gbraid: clickIds.gbraid } : {}),
        ...(clickIds?.wbraid ? { wbraid: clickIds.wbraid } : {}),
      });

      // Pin utm + gclid as PostHog super-properties so every later client event
      // carries the paid source. The signup event itself is captured server-side
      // (utm never reaches it), but registering here keeps the campaign on the
      // person — merged into the user on identify/alias — so the paid funnel can
      // still be sliced by source in PostHog (issue #275). Best-effort and only
      // for present values, so organic traffic stays unstamped.
      //
      // register_once (not register): first-touch wins. register() would
      // overwrite the persisted campaign, so a later landing under a different
      // utm/gclid would re-attribute the signup to the last source — the
      // opposite of the "initial UTM" the docs say to slice by, and inconsistent
      // with touchVisitor's first-touch click-id capture on the Convex side.
      if (!posthog || cancelled) {
        return;
      }
      const campaign = await webAnalyticsSession.resolveCampaignProps(search);
      if (Object.keys(campaign).length > 0) {
        posthog.register_once(campaign);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [posthog, touchVisitor, visitorId]);

  return null;
}

function currentSearch() {
  return typeof window !== "undefined" ? window.location.search : "";
}
